import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Alert, Button, Typography } from 'antd';
import './AppUpdateBanner.css';
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// Firebase
import { db, auth, signInWithEmailAndPassword, signOut } from './firebaseUtils.js';

// Components
import Layout from './Layout.js';
import Dashboard from './Dashboard.js';
import CreateQuote from './CreateQuote.js';
import CreateInvoice from './CreateInvoice.js';
import Quotes from './Quotes.js';
import Invoices from './Invoices.js';
import QuoteDetail from './QuoteDetail.js';
import InvoiceDetail from './InvoiceDetail.js';
import ProformaInvoice from './ProformaInvoice.js';
import Login from './Login.js';
import CreateOrder from './CreateOrder.js';
import Orders from './Orders.js';
import OrderDetail from './OrderDetail.js';
import Spinner from './Spinner.js';
import AccountsDashboard from './AccountsDashboard.js';
import TransactionLedger from './TransactionLedger.js';
import AccountsInvoices from './AccountsInvoices.js';
import AccountsOrders from './AccountsOrders.js';
import CreateMOCPage from './CreateMOCPage.js';
import CreateMICPage from './CreateMICPage.js';
import MaterialMovement from './MaterialMovement.js';
import MaterialInward from './MaterialInward.js';
import ConfirmedOrders from './ConfirmedOrders.js';
import MOCPreview from './MOCPreview.js';
import MICPreview from './MICPreview.js';
import AccountsApprovals from './AccountsApprovals.js';
import OrderApprovalDetail from './OrderApprovalDetail.js';
import PackingList from './PackingList.js';
import PackingDetail from './PackingDetail.js';
import ReadyToDispatch from './ReadyToDispatch.js';
import DCDetail from './DCDetail.js';
import QualityCheckList from './QualityCheckList.js';
import InboundQCList from './InboundQCList.js';
import InboundQCDetail from './InboundQCDetail.js';
import QualityCheckDetail from './QualityCheckDetail.js';
import CreateInvTransfer from './CreateInvTransfer.js';
import InventoryTransferList from './InventoryTransferList.js';
import InventoryTransferPreview from './InventoryTransferPreview.js';
import Inventory from './Inventory.js';
import InventoryDetail from './InventoryDetail.js';
import InTransit from './InTransit.js';
import InTransitDetail from './InTransitDetail.js';
import DeliveredList from './DeliveredList.js';
import ReceivedList from './ReceivedList.js';
import ReceivedDetail from './ReceivedDetail.js';
import DeliveredDetail from './DeliveredDetail.js';
import InwardsQue from './InwardsQue.js';
import InwardsDetail from './InwardsDetail.js';
import InwardsLog from './InwardsLog.js';
import InwardsLogDetail from './InwardsLogDetail.js';
import PickupChallanList from './PickupChallanList.js';
import CreatePickupChallan from './CreatePickupChallan.js';
import PCDetail from './PCDetail.js';
import AdminDashboard from './AdminDashboard.js';
import AdminSettings from './AdminSettings.js';
import AdminProducts from './AdminProducts.js';
import AdminMasterCalc from './AdminMasterCalc.js';
import SalesMasterCalc from './SalesMasterCalc.js';
import AdminWarehouses from './AdminWarehouses.js';
import AdminEmployees from './AdminEmployees.js';
import AdminEmployeeCreate from './AdminEmployeeCreate.js';
import CreateProduct from './CreateProduct.js';
import ProductDetail from './ProductDetail.js';
import SalesOrderApprovalList from './SalesOrderApprovalList.js';
import PaymentRequests from './PaymentRequests.js';
import CreatePaymentRequest from './CreatePaymentRequest.js';
import PaymentRequestPreview from './PaymentRequestPreview.js';
import CreateAdvanceSettlement from './CreateAdvanceSettlement.js';
import AdvanceSettlementPreview from './AdvanceSettlementPreview.js';
import AdvanceSettlements from './AdvanceSettlements.js';
import {
  canAccessConfirmedOrders,
  canAccessInvoices,
  canAccessOrders,
  canAccessQuotes,
  canCreateSalesDocuments,
  hasOrderApprovalAccess,
  isAccountsUser,
  isAdminUser,
  isOpsManager,
  isOpsUser,
  isSalesManager,
  isSalesUser,
  resolveUserRole
} from './accessControl.js';

const APP_BUILD_VERSION = '3.2.7';

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  return String(value).trim().toLowerCase() === 'true';
};

const compareVersionStrings = (a, b) => {
  const normalize = (version) => version
    .split('.')
    .map((part) => Number.parseInt(part, 10) || 0)
    .slice(0, 3);
  const [aParts, bParts] = [normalize(a), normalize(b)];
  for (let i = 0; i < 3; i += 1) {
    if (aParts[i] > bParts[i]) return 1;
    if (aParts[i] < bParts[i]) return -1;
  }

  return 0;
};

const isRemoteVersionNewer = (remoteVersion, currentVersion) => {
  if (!remoteVersion || !currentVersion) return false;
  return compareVersionStrings(remoteVersion, currentVersion) > 0;
};

const AppUpdateBanner = ({ remoteVersion, onReload, onHeightChange }) => {
  const bannerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setIsVisible(true), 2000);
    return () => window.clearTimeout(timer);
  }, []);

  useLayoutEffect(() => {
    const banner = bannerRef.current;
    const measure = () => onHeightChange(isVisible ? banner.getBoundingClientRect().height : 0);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(banner);
    return () => observer.disconnect();
  }, [isVisible, onHeightChange]);

  return (
    <div
      ref={bannerRef}
      className={`app-update-banner${isVisible ? ' app-update-banner--visible' : ''}`}
      aria-label="App update"
    >
      <Alert
        banner
        type="info"
        role="status"
        showIcon={false}
        closable={false}
        className="app-update-banner__alert"
        styles={{
          section: { flex: '0 1 auto', minWidth: 0 },
          actions: { marginInlineStart: 0, flexShrink: 0 }
        }}
        title={
          <Typography.Text className="app-update-banner__message">
            Please update to the latest version{remoteVersion ? ` ${remoteVersion}` : ''}
          </Typography.Text>
        }
        action={
          <Button
            color="blue"
            variant="outlined"
            size="large"
            className="app-update-banner__button"
            onClick={onReload}
          >
            Update Now
          </Button>
        }
      />
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [updateBannerHeight, setUpdateBannerHeight] = useState(0);
  const [remoteBuildVersion, setRemoteBuildVersion] = useState(null);
  const [isUpdateRequired, setIsUpdateRequired] = useState(false);

  // Persistence and Authorization Check
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Double check authorization against Firestore on refresh
        const userData = await checkAuthorization(currentUser.email);
        if (userData) {
          setUser({
            ...currentUser,
            displayName: userData.displayName || currentUser.displayName || currentUser.email,
            role: resolveUserRole(userData, currentUser.email),
            paymentRequestApprovalRoles: Array.isArray(userData.paymentRequestApprovalRoles)
              ? userData.paymentRequestApprovalRoles
              : []
          });
        } else {
          await signOut(auth);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    let unsubscribeConfig = () => { };
    const configRef = doc(db, 'system_config', 'metadata');

    unsubscribeConfig = onSnapshot(configRef, (configSnap) => {
      if (configSnap.exists()) {
        const configData = configSnap.data();
        const remoteVersion = configData?.appBuildVersion || null;
        const shouldForceUpdate = parseBoolean(configData?.appForceUpdate);
        const updateRequired = shouldForceUpdate || isRemoteVersionNewer(remoteVersion, APP_BUILD_VERSION);

        setRemoteBuildVersion(remoteVersion);
        setIsUpdateRequired(updateRequired);
      } else {
        console.warn('App version config not found in system_config/metadata');
        setIsUpdateRequired(false);
      }
    }, (error) => {
      console.error('Version listener failed:', error);
    });

    return () => unsubscribeConfig();
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const checkAuthorization = async (email) => {
    try {
      const userRef = doc(db, "authorized_users", email);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        return userSnap.data(); // Return the whole data object containing the role
      }
      return null;
    } catch (error) {
      console.error("Auth check failed:", error);
      return null;
    }
  };

  const handleLogin = async (email, password) => {
    try {
      const credentials = await signInWithEmailAndPassword(auth, email, password);
      const loggedInUser = credentials.user;

      const userData = await checkAuthorization(loggedInUser.email);

      if (userData) {
        setUser({
          ...loggedInUser,
          displayName: userData.displayName || loggedInUser.displayName || loggedInUser.email,
          role: resolveUserRole(userData, loggedInUser.email),
          paymentRequestApprovalRoles: Array.isArray(userData.paymentRequestApprovalRoles)
            ? userData.paymentRequestApprovalRoles
            : []
        });
      } else {
        await signOut(auth);
        alert("Access Denied: Your email is not whitelisted in the system.");
      }
    } catch (error) {
      console.error("Login failed:", error);
      if (error?.code === 'auth/invalid-credential' || error?.code === 'auth/wrong-password' || error?.code === 'auth/user-not-found') {
        alert("Invalid email or password.");
      } else if (error?.code === 'auth/invalid-email') {
        alert("Please enter a valid email address.");
      } else if (error?.code === 'auth/too-many-requests') {
        alert("Too many failed attempts. Please try again later.");
      } else {
        alert("Login failed. Please try again.");
      }
    }
  };

  const handleLogout = () => {
    signOut(auth).then(() => setUser(null));
  };

  const router = useMemo(() => createBrowserRouter([
    {
      path: "/",
      element: <Layout user={user} handleLogout={handleLogout} db={db} buildVersion={APP_BUILD_VERSION} />,
      children: [
        { path: "/", element: isAccountsUser(user) ? <AccountsDashboard db={db} user={user} /> : (isAdminUser(user) ? <AdminDashboard db={db} user={user} /> : <Dashboard db={db} user={user} />) },
        // Admin routes
        { path: "/admin", element: isAdminUser(user) ? <AdminDashboard db={db} /> : <Navigate to="/" /> },
        { path: "/admin/products", element: isAdminUser(user) ? <AdminProducts db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/products/mastercalc", element: isAdminUser(user) ? <AdminMasterCalc db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/sales/mastercalc", element: isSalesUser(user) ? <SalesMasterCalc db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/products/:id", element: isAdminUser(user) ? <ProductDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/products/new", element: isAdminUser(user) ? <CreateProduct db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/products/edit/:id", element: isAdminUser(user) ? <CreateProduct db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/warehouses", element: isAdminUser(user) ? <AdminWarehouses db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/employees", element: isAdminUser(user) ? <AdminEmployees db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/employees/new", element: isAdminUser(user) ? <AdminEmployeeCreate db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/settings", element: isAdminUser(user) ? <AdminSettings db={db} /> : <Navigate to="/" /> },
        // Sales and Admin accessible Quote routes
        { path: "/quotes/new", element: canCreateSalesDocuments(user) ? <CreateQuote db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/quotes", element: canAccessQuotes(user) ? <Quotes db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/quotes/:id", element: canAccessQuotes(user) ? <QuoteDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/quotes/:id/pi", element: canAccessQuotes(user) ? <ProformaInvoice db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/invoices/new", element: canCreateSalesDocuments(user) ? <CreateInvoice db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/invoices", element: canAccessInvoices(user) ? <Invoices db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/invoices/:id", element: canAccessInvoices(user) ? <InvoiceDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/orders/new", element: canCreateSalesDocuments(user) ? <CreateOrder db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/sales/order-approvals", element: hasOrderApprovalAccess(user) ? <SalesOrderApprovalList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/payments-requests", element: <PaymentRequests db={db} user={user} /> },
        { path: "/payment-requests/approvals", element: (isSalesManager(user) || isOpsManager(user)) ? <PaymentRequests db={db} user={user} view="approval" /> : <Navigate to="/payments-requests" /> },
        { path: "/payment-requests/accounts-all", element: (user?.paymentRequestApprovalRoles?.includes('accounts_check') || user?.paymentRequestApprovalRoles?.includes('accounts_payment')) ? <PaymentRequests db={db} user={user} view="accounts_all" /> : <Navigate to="/payments-requests" /> },
        { path: "/payment-requests/accounts-check", element: <Navigate to="/payment-requests/accounts-all" replace /> },
        { path: "/payment-requests/accounts-payment", element: <Navigate to="/payment-requests/accounts-all" replace /> },
        { path: "/payment-requests/hr-review", element: user?.paymentRequestApprovalRoles?.includes('hr_review') ? <PaymentRequests db={db} user={user} view="hr_review" /> : <Navigate to="/payments-requests" /> },
        { path: "/payment-requests/admin-approval", element: user?.paymentRequestApprovalRoles?.includes('admin_approval') ? <PaymentRequests db={db} user={user} view="admin_approval" /> : <Navigate to="/payments-requests" /> },
        { path: "/payment-requests/settlements", element: <AdvanceSettlements db={db} user={user} /> },
        { path: "/payment-requests/new", element: <CreatePaymentRequest db={db} user={user} /> },
        { path: "/payment-requests/:requestId/edit", element: <CreatePaymentRequest db={db} user={user} /> },
        { path: "/payment-requests/:requestId/settlement/new", element: <CreateAdvanceSettlement db={db} user={user} /> },
        { path: "/payment-requests/:requestId/settlements/:settlementId/edit", element: <CreateAdvanceSettlement db={db} user={user} /> },
        { path: "/payment-requests/:requestId/settlements/:settlementId", element: <AdvanceSettlementPreview db={db} user={user} /> },
        { path: "/payment-requests/:requestId", element: <PaymentRequestPreview db={db} user={user} /> },
        // New Ops Workflow Routes
        { path: "/ops/moc/new", element: isOpsUser(user) ? <CreateMOCPage db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/moc/edit/:mocId", element: isOpsUser(user) ? <CreateMOCPage db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/moc/preview/:mocId", element: (isOpsUser(user) || isAdminUser(user)) ? <MOCPreview db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/mic/new", element: (isOpsUser(user) || isAdminUser(user)) ? <CreateMICPage db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/mic/edit/:micId", element: (isOpsUser(user) || isAdminUser(user)) ? <CreateMICPage db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/mic/preview/:micId", element: (isOpsUser(user) || isAdminUser(user)) ? <MICPreview db={db} user={user} /> : <Navigate to="/" /> },


        { path: "/ops/shipments", element: (isOpsUser(user) || isAdminUser(user)) ? <MaterialMovement db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/mic", element: (isOpsUser(user) || isAdminUser(user)) ? <MaterialInward db={db} /> : <Navigate to="/" /> },
        { path: "/ops/packing", element: (isOpsUser(user) || isAdminUser(user)) ? <PackingList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/packing/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <PackingDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/ready-to-dispatch", element: (isOpsUser(user) || isAdminUser(user)) ? <ReadyToDispatch db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/dispatch/:id", element: (isOpsUser(user) || isAccountsUser(user) || isAdminUser(user)) ? <DCDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/quality-check", element: (isOpsUser(user) || isAdminUser(user)) ? <QualityCheckList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inbound-qc", element: (isOpsUser(user) || isAdminUser(user)) ? <InboundQCList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inbound-qc/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <InboundQCDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/qc/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <QualityCheckDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/in-transit", element: (isOpsUser(user) || isAdminUser(user)) ? <InTransit db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/in-transit/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <InTransitDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/delivered", element: (isOpsUser(user) || isSalesUser(user) || isAccountsUser(user) || isAdminUser(user)) ? <DeliveredList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/received", element: (isOpsUser(user) || isAdminUser(user)) ? <ReceivedList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/delivered/:id", element: (isOpsUser(user) || isSalesUser(user) || isAccountsUser(user) || isAdminUser(user)) ? <DeliveredDetail db={db} /> : <Navigate to="/" /> },
        { path: "/ops/received/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <ReceivedDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inwards-log", element: (isOpsUser(user) || isAdminUser(user)) ? <InwardsLog db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inwards-log/new", element: (isOpsUser(user) || isAdminUser(user)) ? <InwardsLog db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inwards-log/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <InwardsLogDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inbound-que", element: (isOpsUser(user) || isAdminUser(user)) ? <InwardsQue db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inbound-que/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <InwardsDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/pickup-challan", element: (isOpsUser(user) || isAdminUser(user)) ? <PickupChallanList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/pickup-challan/new", element: (isOpsUser(user) || isAdminUser(user)) ? <CreatePickupChallan db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/pickup-challan/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <PCDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/pickup-challan/edit/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <CreatePickupChallan db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory-transfer", element: isOpsUser(user) ? <InventoryTransferList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory", element: isOpsUser(user) ? <Inventory db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory/:id", element: (isOpsUser(user) || isAdminUser(user)) ? <InventoryDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory-transfer/:id", element: isOpsUser(user) ? <InventoryTransferPreview db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory-transfer/new", element: isOpsUser(user) ? <CreateInvTransfer db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory-transfer/edit/:id", element: isOpsUser(user) ? <CreateInvTransfer db={db} user={user} /> : <Navigate to="/" /> },
        // Accounts-only specific module routes (dashboard handled by root path)
        { path: "/accounts/transactions", element: (isAccountsUser(user) || isAdminUser(user)) ? <TransactionLedger db={db} /> : <Navigate to="/" /> },
        { path: "/accounts/invoices", element: (isAccountsUser(user) || isAdminUser(user)) ? <AccountsInvoices db={db} /> : <Navigate to="/" /> },
        { path: "/accounts/orders", element: (isAccountsUser(user) || isAdminUser(user)) ? <AccountsOrders db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/accounts/approvals", element: (isAccountsUser(user) || isAdminUser(user)) ? <AccountsApprovals db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/accounts/approvals/:orderId", element: (isAccountsUser(user) || isAdminUser(user)) ? <OrderApprovalDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/accounts/pickup-challan", element: (isAccountsUser(user) || isAdminUser(user)) ? <PickupChallanList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/accounts/pickup-challan/:id", element: (isAccountsUser(user) || isAdminUser(user)) ? <PCDetail db={db} user={user} /> : <Navigate to="/" /> },
        // Common routes (Order Module has its own internal role filtering)
        { path: "/orders", element: canAccessOrders(user) ? <Orders db={db} user={user} /> : (isAccountsUser(user) ? <Navigate to="/accounts/orders" /> : <Navigate to="/" />) },
        { path: "/orders/confirmed", element: canAccessConfirmedOrders(user) ? <ConfirmedOrders db={db} user={user} /> : <Navigate to="/" /> },

        { path: "/orders/:id", element: isAccountsUser(user) ? <Navigate to="/accounts/orders" /> : <OrderDetail db={db} user={user} /> },
        { path: "*", element: <Navigate to="/" /> },
      ],
    },
  ]), [user, handleLogout]);

  if (loading) {
    return <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spinner /></div>;
  }

  // 1. LOGIN SCREEN (If not logged in)
  if (!user) {
    return <Login handleLogin={handleLogin} />;
  }

  const bannerOffset = isUpdateRequired ? updateBannerHeight : 0;

  // 2. APP SCREEN (If logged in)
  return (
    <div style={{ '--app-update-height': bannerOffset + 'px', paddingTop: bannerOffset }}>
      <Toaster
        position={windowWidth <= 768 ? "top-center" : "top-right"}
        containerStyle={{ top: bannerOffset + 16 }}
      />
      {isUpdateRequired && (
        <AppUpdateBanner
          remoteVersion={remoteBuildVersion}
          onHeightChange={setUpdateBannerHeight}
          onReload={() => window.location.reload()}
        />
      )}
      <RouterProvider router={router} />
    </div>
  );
}

export default App;
